import React, { Dispatch, SetStateAction, useState } from 'react';
import { IItinerary, IKeyStringValue } from 'common/types';
import { Days, getTripInfo } from 'common/utils';
import { IFilterStore, runFilter } from 'list/international/filter/filterStore';
import {
  IExpressingInternationalList,
  IFareTypeList,
  IInternationalContextResult,
  IQueryValue,
  IReceivedInternationalResult,
  ISchedules,
  ISortOption,
  SchFaresMap,
  SelectTypeKeys,
  SortKeys,
  SortOrders,
} from 'list/international/types';
import {
  getDateTime,
  getDuration,
  getInfosFromSch,
  getPriceOfPerson,
  getSchedule,
  getTotalDuration,
} from 'list/international/utils';
import selectTypeStorage from 'storage/internationalSelectType';
import { TripType } from './searchParams';

interface IProps {
  children: React.ReactNode;
  queryVariables: IQueryValue;
}

export interface IInternationalContextState {
  expFlights?: IExpressingInternationalList; // 필터링, 정렬
  orgFlights?: IReceivedInternationalResult; // 원본
  sortBy: ISortOption;
  selectType: SelectTypeKeys;
  setFlightList: (
    originalFlights?: IReceivedInternationalResult,
    filterStore?: IFilterStore,
  ) => void;
  setSortBy: Dispatch<SetStateAction<ISortOption>>;
  sortSchs: (
    schs: string[],
    selectType: SelectTypeKeys,
    flightResult: IInternationalContextResult,
  ) => string[][];
}

export const mapExpFlights = (
  orgFlights: IReceivedInternationalResult | null,
  filterStore: IFilterStore,
  itinerary: IItinerary[],
  tripType: string,
  selectType: SelectTypeKeys,
): IExpressingInternationalList => {
  if (!orgFlights?.results) {
    return {
      airlines: {},
      airports: {},
      fareTypes: {},
      schedules: [],
      schs: [],
      fares: new Map(),
      errors: [],
    };
  }
  const { airlines, airports, fareTypes, schedules, errors } =
    orgFlights.results;
  const filteredFareMap = runFilter(
    filterStore,
    orgFlights,
    itinerary,
    tripType,
    selectType,
  );
  return {
    airlines,
    airports,
    fareTypes,
    schedules,
    errors,
    schs: Array.from(filteredFareMap.keys()),
    fares: filteredFareMap,
  };
};

export const sortMethods = {
  getLowestPrice(fareMap: IFareTypeList, fareType?: string) {
    if (fareType && !fareMap[fareType]) return Number.MAX_SAFE_INTEGER;
    const fares = fareType ? [fareMap[fareType]] : Object.values(fareMap);
    if (!fares.length) return Number.MAX_SAFE_INTEGER;
    return Math.min(
      ...fares.map(([minFare]) => getPriceOfPerson(minFare.Adult, true)),
    );
  },
  getLowestSchedulePrice(
    fares: SchFaresMap,
    scheduleKeys?: string[],
    fareType?: string,
  ) {
    return Math.min(
      ...(scheduleKeys || Object.keys(fares)).map(key =>
        sortMethods.getLowestPrice(fares.get(key)!, fareType),
      ),
    );
  },
  fairSort(schs: string[][], fares: SchFaresMap, order: SortOrders) {
    return schs.sort((a, b) => {
      const getPrice = (sch: string[]) =>
        sortMethods.getLowestPrice(fares.get(sch[0]) || {});
      return order * (getPrice(a) - getPrice(b));
    });
  },
  dateSort(
    schs: string[][],
    schedules: ISchedules[],
    scheduleIndex: number,
    order: SortOrders,
  ) {
    return schs.sort(
      (a, b) =>
        order *
        (getDateTime(a[0], schedules, scheduleIndex) -
          getDateTime(b[0], schedules, scheduleIndex)),
    );
  },
  durationSort(
    schs: string[][],
    schedules: ISchedules[],
    scheduleIndex: number,
    order: SortOrders,
  ) {
    return schs.sort(
      (a, b) =>
        order *
        (getDuration(a[0], schedules, scheduleIndex) -
          getDuration(b[0], schedules, scheduleIndex)),
    );
  },
  totalDurationSort(
    schs: string[][],
    schedules: ISchedules[],
    order: SortOrders,
  ) {
    return schs.sort(
      (a, b) =>
        order *
        (getTotalDuration(a[0], schedules) - getTotalDuration(b[0], schedules)),
    );
  },
  nameSort(
    schs: string[][],
    schedules: ISchedules[],
    scheduleIndex: number,
    order: SortOrders,
    airlines: IKeyStringValue,
  ) {
    const getItem = (sch: string) =>
      airlines[getSchedule(sch, schedules, scheduleIndex)?.detail[0].av];
    return schs.sort(
      (a, b) => order * getItem(a[0])?.localeCompare(getItem(b[0]), 'ko-KR'),
    );
  },
};

const defaultSortBy = {
  key: SortKeys.price,
  order: SortOrders.ascending,
  targetIndex: 0,
};

export const InternationalContext =
  React.createContext<IInternationalContextState>({
    expFlights: undefined,
    orgFlights: undefined,
    sortBy: defaultSortBy,
    selectType: SelectTypeKeys.none,
    setFlightList: () => {},
    setSortBy: () => {},
    sortSchs: () => [],
  });

export const mapConcurrentSchs = (() => {
  let prevSchs: string[] = [];
  let prevRes: string[][] = [];
  return (
    schs: string[],
    fares: SchFaresMap,
    schedules: ISchedules[],
  ): string[][] => {
    if (
      prevRes &&
      prevSchs.length === schs.length &&
      prevSchs.every((sch, index) => sch === schs[index])
    ) {
      return prevRes;
    }
    const schsMap = new Map<
      string,
      { sch: string; time: number; sdt: Days }[]
    >();
    schs.forEach(sch => {
      const { lowestPrice, time, schedule, sdt } = getInfosFromSch(
        sch,
        fares,
        schedules,
      );
      if (!schedule.length) return;
      const isDirect =
        schedule.reduce<number>((a, c) => a + c.length, 0) === schedule.length;
      let itemKey = `${lowestPrice}`;
      let schGroup = schsMap.get(itemKey) || [];
      if (isDirect && schGroup.length) {
        itemKey = `${itemKey}__${Number(itemKey.split('__')[1] || '0') + 1}`;
        schGroup = [];
      }
      schGroup.push({ sch, time, sdt });
      schsMap.set(itemKey, schGroup);
    });
    const newArr = Array.from(schsMap).map(([, schGroup]) => {
      const newGroup = [...schGroup];
      newGroup.sort((a, b) => {
        if (a.time !== b.time) return a.time - b.time;
        return a.sdt.diff(b.sdt);
      });
      return newGroup.map(s => s.sch);
    });
    prevSchs = [...schs];
    prevRes = newArr;
    return prevRes;
  };
})();

export const InternationalContextProvider = ({
  children,
  queryVariables: { itinerary, trip, stayLength, querySelectType },
}: IProps) => {
  const [flights, setFlights] = useState<
    [IReceivedInternationalResult?, IExpressingInternationalList?]
  >([undefined, undefined]);
  const [sortBy, setSortBy] = useState(defaultSortBy);

  const orgTripType = getTripInfo(itinerary);
  const tripTypeOfListView = stayLength ? TripType.OW : orgTripType;
  const { getSelectTypeFromStorage } = selectTypeStorage();
  const selectType =
    tripTypeOfListView === TripType.RT
      ? SelectTypeKeys[querySelectType as keyof typeof SelectTypeKeys] ||
        getSelectTypeFromStorage() ||
        SelectTypeKeys.concurrent
      : SelectTypeKeys.none;

  const setFlightList = (
    originalFlights?: IReceivedInternationalResult,
    _filterStore?: IFilterStore,
  ) => {
    setFlights([
      originalFlights,
      originalFlights && _filterStore
        ? mapExpFlights(
            originalFlights,
            _filterStore,
            itinerary,
            trip,
            selectType,
          )
        : undefined,
    ]);
  };

  const sortSchs = (
    schs: string[],
    selectType: SelectTypeKeys,
    flightResult: IInternationalContextResult,
  ) => {
    const { key, order, targetIndex } = sortBy;
    const { fares, schedules = [], airlines = {} } = flightResult;
    const mappedSchs: string[][] =
      selectType === SelectTypeKeys.concurrent
        ? mapConcurrentSchs(schs, fares, schedules)
        : schs.map(s => [s]);
    if (targetIndex >= schedules.length) return mappedSchs;
    switch (key) {
      case SortKeys.price: {
        return sortMethods.fairSort(mappedSchs, fares, order);
      }
      case SortKeys.date: {
        return sortMethods.dateSort(mappedSchs, schedules, targetIndex, order);
      }
      case SortKeys.duration: {
        return sortMethods.durationSort(
          mappedSchs,
          schedules,
          targetIndex,
          order,
        );
      }
      case SortKeys.depDate: {
        return sortMethods.dateSort(mappedSchs, schedules, 0, order);
      }
      case SortKeys.arrDate: {
        return sortMethods.dateSort(mappedSchs, schedules, 1, order);
      }
      case SortKeys.depDuration: {
        return sortMethods.durationSort(mappedSchs, schedules, 0, order);
      }
      case SortKeys.arrDuration: {
        return sortMethods.durationSort(mappedSchs, schedules, 1, order);
      }
      case SortKeys.name: {
        return sortMethods.nameSort(
          mappedSchs,
          schedules,
          targetIndex,
          order,
          airlines,
        );
      }
      default:
        return mappedSchs;
    }
  };

  return (
    <InternationalContext.Provider
      value={{
        expFlights: flights[1],
        orgFlights: flights[0],
        sortBy,
        selectType,
        setFlightList,
        setSortBy,
        sortSchs,
      }}
    >
      {children}
    </InternationalContext.Provider>
  );
};
